import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight, Home as HomeIcon, Wrench, Sparkles, Wind, Paintbrush, Zap, Trees, Grid3x3, AppWindow, Hammer, HardHat, Plus, TrendingUp, Building2, Stethoscope, GraduationCap, Hotel, Store, Building, Factory, Landmark, Users, Briefcase, MapPin, MonitorSmartphone, Search, Bot, Star, Repeat, Send, Gift, Quote } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { OFFERINGS, STEPS, MARQUEE_WORDS, IMAGES } from "@/data";

const HERO_LINES = ["Win more", "commercial", "contracts."];

const lineParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const lineChild = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 1, ease: [0.22, 1, 0.36, 1] } },
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  return (
    <section ref={ref} data-testid="hero" className="relative h-[100svh] min-h-[640px] overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Commercial skyline at night" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#050507]/60 via-[#050507]/70 to-[#050507]" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#050507]/80 to-transparent" />

      <motion.div style={{ y: overlayY }} className="relative h-full max-w-[1400px] mx-auto px-6 lg:px-12 flex flex-col justify-end pb-20">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="overline text-gold mb-8 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-gold" /> Commercial Contract Acquisition Service
        </motion.div>

        <motion.h1
          variants={lineParent}
          initial="hidden"
          animate="show"
          className="font-serif text-[clamp(3rem,11vw,9rem)] leading-[0.92] tracking-tighter text-white"
        >
          {HERO_LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                variants={lineChild}
                className={`block ${i === 1 ? "italic text-gold" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6 max-w-3xl"
        >
          <p className="text-white/70 text-base md:text-lg leading-relaxed max-w-md">
            We help contractors and service providers build a commercial pipeline that keeps winning — from the first opportunity to lifelong client loyalty.
          </p>
          <Link
            to="/contact"
            data-testid="hero-cta"
            className="group inline-flex items-center gap-3 bg-gold text-black overline px-8 py-4 hover:bg-[#F3E5AB] transition-colors whitespace-nowrap"
          >
            Book a Free Consultation
            <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </Link>
        </motion.div>
      </motion.div>
    </section>
  );
};

const EditorialMarquee = () => (
  <div className="py-14 border-y border-white/10 bg-[#0F0F13]">
    <Marquee speed={40} gradient={false} autoFill>
      {MARQUEE_WORDS.map((w, i) => (
        <span key={i} className="font-serif italic text-2xl md:text-4xl text-white/40 mx-16 flex items-center gap-16">
          {w} <span className="text-gold not-italic text-xl">✦</span>
        </span>
      ))}
    </Marquee>
  </div>
);

const Offerings = () => (
  <section data-testid="offerings" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-28 md:py-40">
    <div className="grid md:grid-cols-12 gap-10 mb-16">
      <Reveal className="md:col-span-5">
        <div className="overline text-gold mb-6">What we do</div>
        <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-none">
          Three ways we grow <span className="italic text-gold">your business</span>.
        </h2>
      </Reveal>
      <Reveal delay={0.15} className="md:col-span-6 md:col-start-7 self-end">
        <p className="text-white/60 text-lg leading-relaxed">
          Everything is built to do one thing: put your business in front of the people who award commercial contracts — and keep you there.
        </p>
      </Reveal>
    </div>

    <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
      {OFFERINGS.map((o, i) => (
        <Reveal key={o.key} delay={i * 0.12} className="h-full">
          <div className="group h-full bg-[#0F0F13] p-10 hover:bg-[#14141a] transition-colors duration-500 flex flex-col">
            <div className="flex items-center justify-between mb-10">
              <span className="overline text-white/40">{o.tag}</span>
              <span className="text-gold font-serif text-2xl">0{i + 1}</span>
            </div>
            <h3 className="font-serif text-3xl tracking-tight mb-5 group-hover:text-gold transition-colors">{o.title}</h3>
            <p className="text-white/60 leading-relaxed flex-1">{o.body}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const ProcessPreview = () => (
  <section data-testid="process-preview" className="bg-[#0F0F13] border-y border-white/10">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-28 md:py-40 grid md:grid-cols-12 gap-14 items-center">
      <Reveal className="md:col-span-6">
        <div className="overline text-gold mb-6">The method</div>
        <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-none mb-8">
          A ten-step engine for <span className="italic text-gold">winning work</span>.
        </h2>
        <p className="text-white/60 text-lg leading-relaxed mb-10 max-w-lg">
          From identifying commercial opportunities to building long-term loyalty, every step is deliberate, tracked, and relentless.
        </p>
        <Link
          to="/services"
          data-testid="process-preview-cta"
          className="group inline-flex items-center gap-3 border border-white/20 text-white overline px-8 py-4 hover:border-gold hover:text-gold transition-colors"
        >
          See the full process <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
        </Link>
      </Reveal>

      <div className="md:col-span-6 space-y-px bg-white/10 border border-white/10">
        {STEPS.slice(0, 5).map((s, i) => (
          <Reveal key={s.n} delay={i * 0.08}>
            <div className="group flex items-center gap-6 bg-[#0F0F13] px-8 py-6 hover:bg-[#14141a] transition-colors">
              <span className="font-serif text-3xl text-gold w-12">{s.n}</span>
              <span className="font-sans text-white/80 group-hover:text-white transition-colors">{s.title}</span>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const IntroImage = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-28 md:py-40 grid md:grid-cols-12 gap-14 items-center">
      <Reveal className="md:col-span-5 order-2 md:order-1">
        <div className="overline text-gold mb-6">Built for contractors</div>
        <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight mb-6">
          You do the work. <br /><span className="italic text-gold">We fill the pipeline.</span>
        </h2>
        <p className="text-white/60 text-lg leading-relaxed mb-8">
          Stop waiting for the phone to ring. We put you in direct contact with property managers, facility managers, business owners, and HOA managers actively making vendor decisions.
        </p>
        <Link to="/about" data-testid="intro-about-link" className="group inline-flex items-center gap-3 text-gold overline">
          Meet Marcus <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
        </Link>
      </Reveal>
      <div ref={ref} className="md:col-span-6 md:col-start-7 order-1 md:order-2 relative">
        <div className="relative overflow-hidden aspect-[4/5] border border-white/10">
          <motion.img style={{ y, scale: 1.12 }} src={IMAGES.handshake} alt="Business handshake" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050507]/60 to-transparent" />
        </div>
        <div className="absolute -bottom-6 -left-6 bg-gold text-black p-6 max-w-[220px] hidden md:block">
          <div className="font-serif text-4xl leading-none">Long-term</div>
          <div className="overline mt-2">client loyalty</div>
        </div>
      </div>
    </section>
  );
};

const CTA = () => (
  <section data-testid="home-cta" className="relative overflow-hidden">
    <img src={IMAGES.hero} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
    <div className="absolute inset-0 bg-[#050507]/80" />
    <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 py-32 text-center">
      <Reveal>
        <div className="overline text-gold mb-8">Ready when you are</div>
        <h2 className="font-serif text-5xl md:text-7xl tracking-tighter leading-none max-w-4xl mx-auto">
          Let's build your commercial <span className="italic text-gold">pipeline</span>.
        </h2>
        <Link
          to="/contact"
          data-testid="home-cta-button"
          className="group inline-flex items-center gap-3 bg-gold text-black overline px-10 py-5 mt-12 hover:bg-[#F3E5AB] transition-colors"
        >
          Book a Free Consultation
          <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const SECTORS = [
  { label: "Roofing", Icon: HomeIcon },
  { label: "Plumbing", Icon: Wrench },
  { label: "Commercial Cleaning", Icon: Sparkles },
  { label: "HVAC", Icon: Wind },
  { label: "Painting", Icon: Paintbrush },
  { label: "Electrical", Icon: Zap },
  { label: "Landscaping", Icon: Trees },
  { label: "Flooring", Icon: Grid3x3 },
  { label: "Window Cleaning", Icon: AppWindow },
  { label: "Restoration", Icon: Hammer },
  { label: "Handyman Services", Icon: HardHat },
  { label: "& Many More", Icon: Plus },
];

const TARGET_PROPERTIES = [
  { label: "Office Buildings", Icon: Building2 },
  { label: "Medical Facilities", Icon: Stethoscope },
  { label: "Schools", Icon: GraduationCap },
  { label: "Hotels", Icon: Hotel },
  { label: "Retail Centers", Icon: Store },
  { label: "Apartment Complexes", Icon: Building },
  { label: "Industrial Facilities", Icon: Factory },
  { label: "Government Buildings", Icon: Landmark },
  { label: "HOAs", Icon: Users },
  { label: "Property Management Companies", Icon: Briefcase },
  { label: "And More...", Icon: Plus },
];

const TargetProperties = () => (
  <section data-testid="target-properties" className="bg-[#0F0F13] border-y border-white/10">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-28 md:py-40">
      <div className="max-w-4xl mb-16">
        <Reveal>
          <div className="overline text-gold mb-6 flex items-center gap-3">
            <span className="h-px w-10 bg-gold" /> Recurring Commercial Revenue
          </div>
          <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
            Stop chasing jobs.<br />
            <span className="italic text-gold">Start building recurring revenue.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="font-serif text-2xl md:text-3xl text-white/85 mt-8 tracking-tight">
            Is your company ready for larger commercial accounts?
          </p>
        </Reveal>
        <Reveal delay={0.22}>
          <p className="text-white/50 text-lg mt-6">These are the types of commercial properties we target:</p>
        </Reveal>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
        {TARGET_PROPERTIES.map((p, i) => (
          <Reveal key={p.label} delay={(i % 4) * 0.06}>
            <div className="group h-full bg-[#0F0F13] px-6 py-8 md:px-8 flex items-center gap-4 hover:bg-[#14141a] transition-colors duration-500">
              <p.Icon size={22} strokeWidth={1.5} className="text-gold shrink-0 group-hover:scale-110 transition-transform" />
              <span className="font-sans text-white/80 group-hover:text-white transition-colors">{p.label}</span>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Sectors = () => (
  <section data-testid="sectors" className="border-y border-white/10">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-28 md:py-36">
      <div className="grid md:grid-cols-12 gap-10 mb-14">
        <Reveal className="md:col-span-6">
          <div className="overline text-gold mb-6">Sectors we serve</div>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-none">
            Built for <span className="italic text-gold">every trade</span>.
          </h2>
        </Reveal>
        <Reveal delay={0.15} className="md:col-span-6 self-end">
          <p className="text-white/60 text-lg leading-relaxed">
            We help contractors and service providers across a wide range of sectors win more commercial contracts — whatever your trade, we build the pipeline.
          </p>
        </Reveal>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
        {SECTORS.map((s, i) => (
          <Reveal key={s.label} delay={(i % 4) * 0.06}>
            <div className="group h-full bg-[#0F0F13] px-6 py-8 md:px-8 flex items-center gap-4 hover:bg-[#14141a] transition-colors duration-500">
              <s.Icon size={22} strokeWidth={1.5} className="text-gold shrink-0 group-hover:scale-110 transition-transform" />
              <span className="font-sans text-white/80 group-hover:text-white transition-colors">{s.label}</span>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ROI_EXAMPLES = [
  {
    n: "01",
    sector: "Commercial Cleaning Company",
    win: "one office building",
    multiple: "36X",
    body: "When we secure a single office building contract, it carries a potential return of 36× your investment.",
  },
  {
    n: "02",
    sector: "Commercial Roofing Company",
    win: "one commercial re-roof",
    multiple: "50X",
    body: "A single commercial re-roofing contract can carry a potential return of 50× your investment.",
  },
];

const GROWTH_TOOLS = [
  { title: "Business Profile Optimization", body: "Google, directories, and social media platforms optimized so you're found everywhere that matters.", Icon: MapPin },
  { title: "Website Creation & Upgrades", body: "Intuitive business websites built and upgraded to turn visitors into commercial leads.", Icon: MonitorSmartphone },
  { title: "World-Class SEO", body: "Our clients are currently experiencing 100+ page-one Google results per month.", Icon: Search },
  { title: "AI Receptionist & Chatbot", body: "AI agents that save you time and money on daily tasks, sales & marketing, phone responses, and follow-ups.", Icon: Bot },
  { title: "Review Generation System", body: "Consistently generate authentic reviews that build trust and win more commercial work.", Icon: Star },
  { title: "Lead Follow-Up Automation", body: "Automated follow-up keeps every lead warm until they're ready to move forward.", Icon: Repeat },
  { title: "Email & Text Marketing", body: "Targeted email and SMS campaigns that keep you top-of-mind and drive repeat business.", Icon: Send },
  { title: "Loyalty & Referral Program", body: "Turn happy clients into a steady, compounding source of new referrals.", Icon: Gift },
  { title: "And More To Come", body: "New transformative solutions are continually added to the growth system.", Icon: Plus },
];

const GrowthSystem = () => (
  <section data-testid="growth-system" className="border-y border-white/10">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-28 md:py-40">
      <div className="grid md:grid-cols-12 gap-10 mb-16">
        <Reveal className="md:col-span-7">
          <div className="overline text-gold mb-6 flex items-center gap-3">
            <span className="h-px w-10 bg-gold" /> Contractor Growth System &amp; Strategy Deployment
          </div>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-none">
            A complete system to <span className="italic text-gold">grow &amp; scale</span>.
          </h2>
        </Reveal>
        <Reveal delay={0.15} className="md:col-span-5 self-end">
          <p className="text-white/60 text-lg leading-relaxed">
            Beyond winning contracts, we deploy a full suite of tools to build your brand, automate your operations, and keep clients coming back.
          </p>
        </Reveal>
      </div>
      <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
        {GROWTH_TOOLS.map((t, i) => (
          <Reveal key={t.title} delay={(i % 3) * 0.1} className="h-full">
            <div className="group h-full bg-[#0F0F13] p-10 hover:bg-[#14141a] transition-colors duration-500 flex flex-col">
              <t.Icon size={26} strokeWidth={1.5} className="text-gold mb-6 group-hover:scale-110 transition-transform" />
              <h3 className="font-serif text-2xl tracking-tight mb-3 group-hover:text-gold transition-colors">{t.title}</h3>
              <p className="text-white/60 leading-relaxed">{t.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ROI = () => (
  <section data-testid="roi" className="bg-[#0F0F13] border-y border-white/10">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-28 md:py-40">
      <Reveal>
        <div className="overline text-gold mb-6 flex items-center gap-3">
          <span className="h-px w-10 bg-gold" /> Example ROI
        </div>
        <h2 className="font-serif text-4xl md:text-6xl tracking-tighter leading-[0.95] max-w-4xl uppercase">
          One contract can <span className="italic text-gold lowercase">change your business</span>.
        </h2>
      </Reveal>

      <div className="mt-16 space-y-px bg-white/10 border border-white/10">
        {ROI_EXAMPLES.map((e, i) => (
          <Reveal key={e.n} delay={i * 0.1}>
            <div data-testid={`roi-example-${e.n}`} className="bg-[#0F0F13] grid md:grid-cols-12 gap-10 p-10 md:p-14 items-center">
              <div className="md:col-span-6">
                <div className="overline text-white/40 mb-4">Example #{e.n} · {e.sector}</div>
                <h3 className="font-serif text-3xl md:text-4xl tracking-tight mb-4">Secure {e.win}.</h3>
                <p className="text-white/60 text-lg leading-relaxed max-w-md">{e.body}</p>
              </div>
              <div className="md:col-span-6 flex md:justify-end items-center gap-5">
                <TrendingUp className="text-gold hidden md:block shrink-0" size={44} strokeWidth={1.25} />
                <div className="md:text-right">
                  <div className="font-serif text-7xl md:text-8xl lg:text-9xl leading-none text-gold">{e.multiple}</div>
                  <div className="overline text-white/50 mt-2">potential return on investment</div>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal>
        <p className="text-white/30 text-xs mt-6">Illustrative example based on typical commercial contract value. Actual results vary by sector and contract size.</p>
      </Reveal>
    </div>
  </section>
);

const TESTIMONIALS = [
  { quote: "We hired Marcus to help us get more commercial jobs and we are very happy with the results. We've had one of the best summers yet because of his efforts. Not only did we land several large commercial accounts, he also increased our residential contracts.", name: "S. Jones", company: "S & B Contractors" },
  { quote: "We purchased a cleaning business that had about 19 active accounts. As soon as ownership changed hands, four of the accounts dropped, so it put us in a bad situation. I hired Marcus to get us new cleaning accounts and he landed us six new accounts in the first two months. I would definitely recommend his services.", name: "Gene", company: "G & R Cleaning" },
  { quote: "I started a new pressure washing business and I literally had no clients or jobs. I hired Marcus to help get jobs coming in and he got me several fence, deck and patio jobs in the first two weeks. He also negotiated with two restaurants and got us two really nice hood and range exhaust cleaning jobs. He's the best.", name: "Regan", company: "Utopia Power Clean" },
  { quote: "My son and I hired Marcus to help get jobs for our small office cleaning and maid service company. He got us a dentist office the first week, and a long-term agreement with a property management company to clean apartments when tenants move out. Hiring Marcus is the best investment we could have made.", name: "Bessie L.", company: "Bessie's Maid Service" },
  { quote: "We were only providing residential painting services until Marcus helped us land an apartment complex that was such a big job that I had to hire help. We appreciate his persistence. Marcus even went above and beyond our expectations and his scope of services and was personally able to collect on two delinquent painting accounts we had been trying to get payment from for over a year.", name: "L. Parra", company: "L.P. Painting" },
];

const Testimonials = () => (
  <section data-testid="testimonials" className="border-y border-white/10">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-28 md:py-40">
      <div className="grid md:grid-cols-12 gap-10 mb-16">
        <Reveal className="md:col-span-7">
          <div className="overline text-gold mb-6 flex items-center gap-3">
            <span className="h-px w-10 bg-gold" /> Client Results
          </div>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-none">
            Contractors winning <span className="italic text-gold">real work</span>.
          </h2>
        </Reveal>
      </div>
      <div className="grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={t.name} delay={(i % 2) * 0.12} className="h-full">
            <div data-testid={`testimonial-${i}`} className="h-full bg-[#0F0F13] p-10 md:p-12 flex flex-col">
              <Quote size={32} className="text-gold mb-6 shrink-0" />
              <p className="text-white/80 text-lg leading-relaxed flex-1">{t.quote}</p>
              <div className="mt-8 pt-6 border-t border-white/10">
                <div className="font-serif text-xl text-white">{t.name}</div>
                <div className="overline text-white/40 mt-1">{t.company}</div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <EditorialMarquee />
      <Offerings />
      <TargetProperties />
      <Sectors />
      <ProcessPreview />
      <GrowthSystem />
      <ROI />
      <IntroImage />
      <Testimonials />
      <CTA />
    </div>
  );
}
